/**
 * A real QR encoder, because 9.3 asks for a code "readable by an ordinary
 * scanner" and a placeholder pattern is not.
 *
 * Byte mode, versions 1 to 10, all four error-correction levels — comfortably
 * more than a part number, a URL or a JSON identifier needs. The construction
 * follows ISO/IEC 18004: encode, split into Reed-Solomon blocks, interleave,
 * lay the modules out on the matrix, then try all eight masks and keep the one
 * the standard's penalty rules like best.
 */

export type QrErrorLevel = "L" | "M" | "Q" | "H";

export interface QrCode {
  size: number;
  /** Row-major, `true` where the module is dark. */
  modules: boolean[][];
  version: number;
  level: QrErrorLevel;
}

/* -------------------------------------------------------------------------- */
/* Galois field                                                                */
/* -------------------------------------------------------------------------- */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let value = 1;
  for (let index = 0; index < 255; index += 1) {
    EXP[index] = value;
    LOG[value] = index;
    value <<= 1;
    // The field is GF(2^8) with the QR primitive polynomial 0x11d.
    if (value & 0x100) value ^= 0x11d;
  }
  for (let index = 255; index < 512; index += 1) EXP[index] = EXP[index - 255]!;
})();

function multiply(left: number, right: number): number {
  if (left === 0 || right === 0) return 0;
  return EXP[LOG[left]! + LOG[right]!]!;
}

/** Generator polynomial for `degree` error-correction codewords. */
function generatorPolynomial(degree: number): Uint8Array {
  let polynomial = new Uint8Array([1]);
  for (let index = 0; index < degree; index += 1) {
    const next = new Uint8Array(polynomial.length + 1);
    for (let position = 0; position < polynomial.length; position += 1) {
      next[position] = (next[position]! ^ multiply(polynomial[position]!, EXP[index]!)) & 0xff;
      next[position + 1] = (next[position + 1]! ^ polynomial[position]!) & 0xff;
    }
    polynomial = next;
  }
  return polynomial;
}

function errorCorrection(data: Uint8Array, count: number): Uint8Array {
  const generator = generatorPolynomial(count);
  const remainder = new Uint8Array(count);
  for (const byte of data) {
    const factor = byte ^ remainder[0]!;
    remainder.copyWithin(0, 1);
    remainder[count - 1] = 0;
    for (let index = 0; index < count; index += 1) {
      remainder[index] = (remainder[index]! ^ multiply(generator[index + 1]!, factor)) & 0xff;
    }
  }
  return remainder;
}

/* -------------------------------------------------------------------------- */
/* Version tables                                                              */
/* -------------------------------------------------------------------------- */

/** [ec codewords per block, blocks in group 1, data per block, blocks in group 2, data per block]. */
type BlockSpec = [number, number, number, number, number];

const BLOCKS: Record<QrErrorLevel, BlockSpec[]> = {
  L: [
    [7, 1, 19, 0, 0], [10, 1, 34, 0, 0], [15, 1, 55, 0, 0], [20, 1, 80, 0, 0],
    [26, 1, 108, 0, 0], [18, 2, 68, 0, 0], [20, 2, 78, 0, 0], [24, 2, 97, 0, 0],
    [30, 2, 116, 0, 0], [18, 2, 68, 2, 69],
  ],
  M: [
    [10, 1, 16, 0, 0], [16, 1, 28, 0, 0], [26, 1, 44, 0, 0], [18, 2, 32, 0, 0],
    [24, 2, 43, 0, 0], [16, 4, 27, 0, 0], [18, 4, 31, 0, 0], [22, 2, 38, 2, 39],
    [22, 3, 36, 2, 37], [26, 4, 43, 1, 44],
  ],
  Q: [
    [13, 1, 13, 0, 0], [22, 1, 22, 0, 0], [18, 2, 17, 0, 0], [26, 2, 24, 0, 0],
    [18, 2, 15, 2, 16], [24, 4, 19, 0, 0], [18, 2, 14, 4, 15], [22, 4, 18, 2, 19],
    [20, 4, 16, 4, 17], [24, 6, 19, 2, 20],
  ],
  H: [
    [17, 1, 9, 0, 0], [28, 1, 16, 0, 0], [22, 2, 13, 0, 0], [16, 4, 9, 0, 0],
    [22, 2, 11, 2, 12], [28, 4, 15, 0, 0], [26, 4, 13, 1, 14], [26, 4, 14, 2, 15],
    [24, 4, 12, 4, 13], [28, 6, 15, 2, 16],
  ],
};

/** Alignment-pattern centre coordinates, indexed by version. */
const ALIGNMENT: number[][] = [
  [], [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34],
  [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];

const EC_BITS: Record<QrErrorLevel, number> = { L: 0b01, M: 0b00, Q: 0b11, H: 0b10 };

const MAX_VERSION = 10;

function dataCodewords(version: number, level: QrErrorLevel): number {
  const [, group1, data1, group2, data2] = BLOCKS[level][version - 1]!;
  return group1 * data1 + group2 * data2;
}

function characterCountBits(version: number): number {
  return version < 10 ? 8 : 16;
}

function chooseVersion(byteLength: number, level: QrErrorLevel): number {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    const capacity = dataCodewords(version, level) * 8 - 4 - characterCountBits(version);
    if (byteLength * 8 <= capacity) return version;
  }
  throw new Error("O conteúdo é longo demais para um QR de até versão 10.");
}

/* -------------------------------------------------------------------------- */
/* Encoding                                                                    */
/* -------------------------------------------------------------------------- */

class BitBuffer {
  readonly bits: number[] = [];

  push(value: number, length: number): void {
    for (let index = length - 1; index >= 0; index -= 1) {
      this.bits.push((value >>> index) & 1);
    }
  }

  toBytes(): Uint8Array {
    const bytes = new Uint8Array(Math.ceil(this.bits.length / 8));
    for (const [index, bit] of this.bits.entries()) {
      if (bit) bytes[index >>> 3]! |= 0x80 >>> (index & 7);
    }
    return bytes;
  }
}

export function encodeQr(text: string, level: QrErrorLevel = "M"): QrCode {
  const payload = new TextEncoder().encode(text);
  const version = chooseVersion(payload.length, level);
  const capacityBytes = dataCodewords(version, level);

  const buffer = new BitBuffer();
  buffer.push(0b0100, 4); // byte mode
  buffer.push(payload.length, characterCountBits(version));
  for (const byte of payload) buffer.push(byte, 8);

  const capacityBits = capacityBytes * 8;
  // Terminator, then pad to a byte boundary, then the standard pad bytes.
  const terminator = Math.min(4, capacityBits - buffer.bits.length);
  if (terminator > 0) buffer.push(0, terminator);
  while (buffer.bits.length % 8 !== 0) buffer.bits.push(0);
  const bytes = Array.from(buffer.toBytes());
  const padBytes = [0xec, 0x11];
  let padIndex = 0;
  while (bytes.length < capacityBytes) {
    bytes.push(padBytes[padIndex % 2]!);
    padIndex += 1;
  }

  const interleaved = interleave(Uint8Array.from(bytes), version, level);
  return layout(interleaved, version, level);
}

/**
 * Splits the data into the version's blocks, computes each block's error
 * correction, then interleaves both — data column-wise across blocks, then the
 * error-correction words the same way. The interleaving is what lets a scanner
 * lose a whole corner and still recover.
 */
function interleave(data: Uint8Array, version: number, level: QrErrorLevel): Uint8Array {
  const [ecCount, group1, data1, group2, data2] = BLOCKS[level][version - 1]!;
  const blocks: Uint8Array[] = [];
  let cursor = 0;
  for (let index = 0; index < group1; index += 1) {
    blocks.push(data.slice(cursor, cursor + data1));
    cursor += data1;
  }
  for (let index = 0; index < group2; index += 1) {
    blocks.push(data.slice(cursor, cursor + data2));
    cursor += data2;
  }
  const ecBlocks = blocks.map((block) => errorCorrection(block, ecCount));

  const out: number[] = [];
  const longest = Math.max(...blocks.map((block) => block.length));
  for (let position = 0; position < longest; position += 1) {
    for (const block of blocks) {
      if (position < block.length) out.push(block[position]!);
    }
  }
  for (let position = 0; position < ecCount; position += 1) {
    for (const block of ecBlocks) out.push(block[position]!);
  }
  return Uint8Array.from(out);
}

/* -------------------------------------------------------------------------- */
/* Matrix                                                                      */
/* -------------------------------------------------------------------------- */

function layout(payload: Uint8Array, version: number, level: QrErrorLevel): QrCode {
  const size = version * 4 + 17;
  const modules: Array<Array<boolean | null>> = Array.from(
    { length: size },
    () => Array.from({ length: size }, () => null),
  );

  const placeFinder = (row: number, column: number): void => {
    for (let dy = -1; dy <= 7; dy += 1) {
      for (let dx = -1; dx <= 7; dx += 1) {
        const y = row + dy;
        const x = column + dx;
        if (y < 0 || x < 0 || y >= size || x >= size) continue;
        const onRing = (dy >= 0 && dy <= 6 && (dx === 0 || dx === 6))
          || (dx >= 0 && dx <= 6 && (dy === 0 || dy === 6));
        const inCore = dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4;
        modules[y]![x] = onRing || inCore;
      }
    }
  };
  placeFinder(0, 0);
  placeFinder(0, size - 7);
  placeFinder(size - 7, 0);

  for (let index = 8; index < size - 8; index += 1) {
    const dark = index % 2 === 0;
    modules[6]![index] = dark;
    modules[index]![6] = dark;
  }

  for (const row of ALIGNMENT[version] ?? []) {
    for (const column of ALIGNMENT[version] ?? []) {
      // The three finder corners already own their space.
      if ((row === 6 && column === 6)
        || (row === 6 && column === size - 7)
        || (row === size - 7 && column === 6)) continue;
      for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
          modules[row + dy]![column + dx] = Math.max(Math.abs(dy), Math.abs(dx)) !== 1;
        }
      }
    }
  }

  modules[size - 8]![8] = true;

  // Format and version areas are reserved now and filled once the mask is known.
  const reserved = new Set<string>();
  for (let index = 0; index < 9; index += 1) {
    reserved.add(`8,${index}`);
    reserved.add(`${index},8`);
  }
  for (let index = 0; index < 8; index += 1) {
    reserved.add(`8,${size - 1 - index}`);
    reserved.add(`${size - 1 - index},8`);
  }
  if (version >= 7) {
    for (let index = 0; index < 6; index += 1) {
      for (let offset = 0; offset < 3; offset += 1) {
        reserved.add(`${index},${size - 11 + offset}`);
        reserved.add(`${size - 11 + offset},${index}`);
      }
    }
  }
  for (const key of reserved) {
    const [row, column] = key.split(",").map(Number) as [number, number];
    if (modules[row]![column] === null) modules[row]![column] = false;
  }

  // Snapshot of which cells are function patterns, so masking skips them.
  const functional = modules.map((row) => row.map((cell) => cell !== null));

  let bitIndex = 0;
  const nextBit = (): boolean => {
    const byte = payload[bitIndex >>> 3];
    const bit = byte === undefined ? false : ((byte >>> (7 - (bitIndex & 7))) & 1) === 1;
    bitIndex += 1;
    return bit;
  };

  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    // Column 6 is the vertical timing pattern and is skipped entirely.
    if (right === 6) right = 5;
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step;
      for (const column of [right, right - 1]) {
        if (functional[row]![column]) continue;
        modules[row]![column] = nextBit();
      }
    }
    upward = !upward;
  }

  let best: { mask: number; grid: boolean[][]; penalty: number } | null = null;
  for (let mask = 0; mask < 8; mask += 1) {
    const grid = modules.map((row, y) => row.map((cell, x) =>
      functional[y]![x] ? Boolean(cell) : Boolean(cell) !== maskAt(mask, y, x)));
    writeFormat(grid, level, mask, size);
    if (version >= 7) writeVersion(grid, version, size);
    const penalty = scorePenalty(grid, size);
    if (!best || penalty < best.penalty) best = { mask, grid, penalty };
  }

  return { size, modules: best!.grid, version, level };
}

function maskAt(mask: number, row: number, column: number): boolean {
  switch (mask) {
    case 0: return (row + column) % 2 === 0;
    case 1: return row % 2 === 0;
    case 2: return column % 3 === 0;
    case 3: return (row + column) % 3 === 0;
    case 4: return (Math.floor(row / 2) + Math.floor(column / 3)) % 2 === 0;
    case 5: return ((row * column) % 2) + ((row * column) % 3) === 0;
    case 6: return (((row * column) % 2) + ((row * column) % 3)) % 2 === 0;
    default: return (((row + column) % 2) + ((row * column) % 3)) % 2 === 0;
  }
}

function writeFormat(grid: boolean[][], level: QrErrorLevel, mask: number, size: number): void {
  const data = (EC_BITS[level] << 3) | mask;
  let remainder = data << 10;
  for (let index = 4; index >= 0; index -= 1) {
    if (remainder & (1 << (index + 10))) remainder ^= 0b10100110111 << index;
  }
  const bits = ((data << 10) | remainder) ^ 0b101010000010010;

  for (let index = 0; index < 15; index += 1) {
    const bit = ((bits >>> index) & 1) === 1;
    // Copy one: around the top-left finder.
    if (index < 6) grid[index]![8] = bit;
    else if (index === 6) grid[7]![8] = bit;
    else if (index === 7) grid[8]![8] = bit;
    else if (index === 8) grid[8]![7] = bit;
    else grid[8]![14 - index] = bit;
    // Copy two: split between the other two finders.
    if (index < 8) grid[8]![size - 1 - index] = bit;
    else grid[size - 15 + index]![8] = bit;
  }
}

function writeVersion(grid: boolean[][], version: number, size: number): void {
  let remainder = version << 12;
  for (let index = 5; index >= 0; index -= 1) {
    if (remainder & (1 << (index + 12))) remainder ^= 0b1111100100101 << index;
  }
  const bits = (version << 12) | remainder;
  for (let index = 0; index < 18; index += 1) {
    const bit = ((bits >>> index) & 1) === 1;
    const row = Math.floor(index / 3);
    const column = index % 3;
    grid[row]![size - 11 + column] = bit;
    grid[size - 11 + column]![row] = bit;
  }
}

/** The four penalty rules from the standard, used to pick the mask. */
function scorePenalty(grid: readonly boolean[][], size: number): number {
  let penalty = 0;

  const runPenalty = (read: (index: number) => boolean): void => {
    let run = 1;
    for (let index = 1; index < size; index += 1) {
      if (read(index) === read(index - 1)) {
        run += 1;
        if (run === 5) penalty += 3;
        else if (run > 5) penalty += 1;
      } else {
        run = 1;
      }
    }
  };
  for (let row = 0; row < size; row += 1) runPenalty((index) => grid[row]![index]!);
  for (let column = 0; column < size; column += 1) runPenalty((index) => grid[index]![column]!);

  for (let row = 0; row < size - 1; row += 1) {
    for (let column = 0; column < size - 1; column += 1) {
      const first = grid[row]![column]!;
      if (first === grid[row]![column + 1] && first === grid[row + 1]![column] && first === grid[row + 1]![column + 1]) {
        penalty += 3;
      }
    }
  }

  const pattern = [true, false, true, true, true, false, true, false, false, false, false];
  const matches = (read: (index: number) => boolean, start: number): boolean =>
    pattern.every((expected, offset) => read(start + offset) === expected);
  const reversed = [...pattern].reverse();
  const matchesReversed = (read: (index: number) => boolean, start: number): boolean =>
    reversed.every((expected, offset) => read(start + offset) === expected);
  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column + 11 <= size; column += 1) {
      const read = (index: number): boolean => grid[row]![index]!;
      if (matches(read, column) || matchesReversed(read, column)) penalty += 40;
    }
  }
  for (let column = 0; column < size; column += 1) {
    for (let row = 0; row + 11 <= size; row += 1) {
      const read = (index: number): boolean => grid[index]![column]!;
      if (matches(read, row) || matchesReversed(read, row)) penalty += 40;
    }
  }

  let dark = 0;
  for (const row of grid) {
    for (const cell of row) if (cell) dark += 1;
  }
  const ratio = (dark * 100) / (size * size);
  penalty += Math.floor(Math.abs(ratio - 50) / 5) * 10;
  return penalty;
}

/* -------------------------------------------------------------------------- */
/* Output                                                                      */
/* -------------------------------------------------------------------------- */

/** The code as an SVG path, one rectangle per dark module, in module units. */
export function qrToPath(code: QrCode): string {
  const parts: string[] = [];
  for (let row = 0; row < code.size; row += 1) {
    for (let column = 0; column < code.size; column += 1) {
      if (code.modules[row]![column]) parts.push(`M${column} ${row}h1v1h-1z`);
    }
  }
  return parts.join("");
}

/**
 * CODE 128 subset B, for the one-dimensional codes the label designer already
 * offers. Encoded as bar widths in module units, starting with a bar.
 */
export function encodeCode128(text: string): number[] {
  const PATTERNS = CODE128_PATTERNS;
  const values: number[] = [104]; // Start B
  for (const character of text) {
    const code = character.charCodeAt(0);
    // Subset B covers 32..127; anything outside becomes a space rather than
    // producing a code a scanner would read as something else.
    values.push(code >= 32 && code <= 127 ? code - 32 : 0);
  }
  const checksum = values.reduce(
    (total, value, index) => total + value * (index === 0 ? 1 : index),
    0,
  ) % 103;
  values.push(checksum, 106); // checksum then Stop
  const widths: number[] = [];
  for (const value of values) {
    const pattern = PATTERNS[value];
    if (!pattern) continue;
    for (const width of pattern) widths.push(width);
  }
  return widths;
}

/**
 * Bar and space widths for each CODE 128 value, six numbers per symbol. Values
 * 0..102 are the data set, 103..105 the start codes and 106 the stop pattern
 * (which carries a seventh element).
 */
const CODE128_PATTERNS: number[][] = [
  [2,1,2,2,2,2],[2,2,2,1,2,2],[2,2,2,2,2,1],[1,2,1,2,2,3],[1,2,1,3,2,2],[1,3,1,2,2,2],
  [1,2,2,2,1,3],[1,2,2,3,1,2],[1,3,2,2,1,2],[2,2,1,2,1,3],[2,2,1,3,1,2],[2,3,1,2,1,2],
  [1,1,2,2,3,2],[1,2,2,1,3,2],[1,2,2,2,3,1],[1,1,3,2,2,2],[1,2,3,1,2,2],[1,2,3,2,2,1],
  [2,2,3,2,1,1],[2,2,1,1,3,2],[2,2,1,2,3,1],[2,1,3,2,1,2],[2,2,3,1,1,2],[3,1,2,1,3,1],
  [3,1,1,2,2,2],[3,2,1,1,2,2],[3,2,1,2,2,1],[3,1,2,2,1,2],[3,2,2,1,1,2],[3,2,2,2,1,1],
  [2,1,2,1,2,3],[2,1,2,3,2,1],[2,3,2,1,2,1],[1,1,1,3,2,3],[1,3,1,1,2,3],[1,3,1,3,2,1],
  [1,1,2,3,1,3],[1,3,2,1,1,3],[1,3,2,3,1,1],[2,1,1,3,1,3],[2,3,1,1,1,3],[2,3,1,3,1,1],
  [1,1,2,1,3,3],[1,1,2,3,3,1],[1,3,2,1,3,1],[1,1,3,1,2,3],[1,1,3,3,2,1],[1,3,3,1,2,1],
  [3,1,3,1,2,1],[2,1,1,3,3,1],[2,3,1,1,3,1],[2,1,3,1,1,3],[2,1,3,3,1,1],[2,1,3,1,3,1],
  [3,1,1,1,2,3],[3,1,1,3,2,1],[3,3,1,1,2,1],[3,1,2,1,1,3],[3,1,2,3,1,1],[3,3,2,1,1,1],
  [3,1,4,1,1,1],[2,2,1,4,1,1],[4,3,1,1,1,1],[1,1,1,2,2,4],[1,1,1,4,2,2],[1,2,1,1,2,4],
  [1,2,1,4,2,1],[1,4,1,1,2,2],[1,4,1,2,2,1],[1,1,2,2,1,4],[1,1,2,4,1,2],[1,2,2,1,1,4],
  [1,2,2,4,1,1],[1,4,2,1,1,2],[1,4,2,2,1,1],[2,4,1,2,1,1],[2,2,1,1,1,4],[4,1,3,1,1,1],
  [2,4,1,1,1,2],[1,3,4,1,1,1],[1,1,1,2,4,2],[1,2,1,1,4,2],[1,2,1,2,4,1],[1,1,4,2,1,2],
  [1,2,4,1,1,2],[1,2,4,2,1,1],[4,1,1,2,1,2],[4,2,1,1,1,2],[4,2,1,2,1,1],[2,1,2,1,4,1],
  [2,1,4,1,2,1],[4,1,2,1,2,1],[1,1,1,1,4,3],[1,1,1,3,4,1],[1,3,1,1,4,1],[1,1,4,1,1,3],
  [1,1,4,3,1,1],[4,1,1,1,1,3],[4,1,1,3,1,1],[1,1,3,1,4,1],[1,1,4,1,3,1],[3,1,1,1,4,1],
  [4,1,1,1,3,1],[2,1,1,4,1,2],[2,1,1,2,1,4],[2,1,1,2,3,2],[2,3,3,1,1,1,2],
];
